import React from 'react';
import classNames from 'classnames';
import './CoachingAgenda.styl';
import CoachingAgendaEmpty from 'components/dashboard/CoachingAgendaEmpty';
import ManagerRecommendedCoaching from 'components/dashboard/manager/ManagerRecommendedCoaching';
import UpcomingAgendaColumn from 'components/dashboard/UpcomingAgendaColumn';
import CoachPendingAgendaColumn from 'components/dashboard/CoachPendingAgendaColumn';
import RepPendingAgendaColumn from 'components/dashboard/RepPendingAgendaColumn';
import LongTermCoachingBanner from 'components/dashboard/LongTermCoachingBanner';
import RecentMeetingsColumn from 'components/dashboard/RecentMeetingsColumn';
import Loading from 'components/pure/utils/Loading';
import SimpleDropdown, {SimpleDropdownOption} from 'charts/components/forms/SimpleDropdown';
import Button from '@material-ui/core/Button';
import {useLocationState} from 'use-location-state';
import {List} from 'immutable';
import CoachingReview from 'records/CoachingReview';
import InProgressReview from 'records/InProgressReview';

const AGENDA_ITEMS_PER_COLUMN = 3;

interface Props {
  lastCompletePlanLoading: boolean;
  upcomingReviewsLoading: boolean;
  noPreviousPlans: boolean;
  noRecommendations: boolean;
  adHocOnly: boolean;
  sessionUser: any; // PropTypes.instanceOf(User),
  readyReviews: List<CoachingReview | InProgressReview>;
  coachReviews: any; // PropTypes.instanceOf(List), // List<CoachingReview>
  repReviews: any; // PropTypes.instanceOf(List), // List<CoachingReview>
  plans: any;
  hideSuggested: boolean;
  refreshCoachingData: boolean;
  location: any;
  hasRenderedDashv2Gif: boolean;
  onEditPlan: any;
  reviewTypes: any;
  onSetReviewType: any;
}

const CoachingAgenda = (props: Props) => {
  const {
    plans,
    noRecommendations,
    hideSuggested,
    adHocOnly,
    readyReviews,
    coachReviews,
    repReviews,
    sessionUser,
    lastCompletePlanLoading,
    upcomingReviewsLoading,
    refreshCoachingData,
    location,
    hasRenderedDashv2Gif,
    onEditPlan,
    reviewTypes,
    onSetReviewType,
  } = props;

  const atLeastOneColumnHasMoreMeetingsToShow =
    readyReviews?.size > AGENDA_ITEMS_PER_COLUMN ||
    coachReviews?.size > AGENDA_ITEMS_PER_COLUMN ||
    repReviews?.size > AGENDA_ITEMS_PER_COLUMN;

  const [showAll, updateShowAll] = useLocationState('agendaShowAll', false);
  const toggleShowAll = () => {
    updateShowAll(!showAll);

    // Always default to sort by date when this is toggled.
    updateSortBy(sortBySelections[0]);
  };

  const sortBySelections: SimpleDropdownOption[] = [
    {id: 1, name: 'Date'},
    {id: 2, name: 'Rep'},
  ];
  const [sortBy, updateSortBy] = useLocationState('agendaSortBy', sortBySelections[0]);
  const sort = review => (sortBy.name === 'Rep' ? review.rep.firstName : review.scheduleAt);

  if (!hasRenderedDashv2Gif) {
    return null;
  }
  if (lastCompletePlanLoading || upcomingReviewsLoading) {
    return <Loading />;
  }

  const renderLongTermBanner = adHocOnly;
  const allEmpty =
    !renderLongTermBanner &&
    readyReviews.isEmpty() &&
    coachReviews.isEmpty() &&
    repReviews.isEmpty();

  if (allEmpty) {
    return <CoachingAgendaEmpty {...props} />;
  }

  const passedInState = location && location.state;
  const selectedPlanId = passedInState ? passedInState.selectedPlanId : 0;
  const commonProps = {
    onEditPlan,
    sessionUser,
    selectedPlanId,
    refreshCoachingData,
    reviewTypes,
    onSetReviewType,
    plans,
  };

  return (
    <div className={classNames('coaching-agenda', !hideSuggested && 'extra-wide')}>
      <div className="title-bar">
        <h2 className="title">Coaching Meetings</h2>
        {atLeastOneColumnHasMoreMeetingsToShow && showAll && (
          <div className="title-bar title-bar-control">
            <span className="sort-controls">
              <p>Sort by:</p>
              <SimpleDropdown
                onSelect={updateSortBy}
                width={100}
                selections={sortBySelections}
                value={sortBy}
              />
            </span>
          </div>
        )}
        {/* Show the all/upcoming toggle button */}
        {atLeastOneColumnHasMoreMeetingsToShow && (
          <Button variant="text" className="title-bar-control" onClick={toggleShowAll}>
            {showAll ? 'Show Only Upcoming' : 'Show All Meetings'}
          </Button>
        )}
        {/* Otherwise show the static text that all meetings are currently visible */}
        {!atLeastOneColumnHasMoreMeetingsToShow && (
          <p className="title-bar-control">Showing all meetings</p>
        )}
      </div>
      <div className="coaching-agenda-row">
        <section className="agenda-columns">
          {renderLongTermBanner
            ? [
                <RecentMeetingsColumn key="recent" plans={plans} />,
                <LongTermCoachingBanner
                  key="banner"
                  noRecommendations={noRecommendations}
                  hideSuggested={hideSuggested}
                />,
              ]
            : [
                <UpcomingAgendaColumn
                  key="upcoming"
                  reviews={readyReviews
                    .sortBy(sort)
                    .take(showAll ? Number.MAX_VALUE : AGENDA_ITEMS_PER_COLUMN)}
                  {...commonProps}
                />,
                <CoachPendingAgendaColumn
                  key="coach"
                  reviews={coachReviews
                    .sortBy(sort)
                    .take(showAll ? Number.MAX_VALUE : AGENDA_ITEMS_PER_COLUMN)}
                  {...commonProps}
                />,
                <RepPendingAgendaColumn
                  key="rep"
                  reviews={repReviews
                    .sortBy(sort)
                    .take(showAll ? Number.MAX_VALUE : AGENDA_ITEMS_PER_COLUMN)}
                  {...commonProps}
                />,
              ]}
        </section>
        {!hideSuggested && (
          <ManagerRecommendedCoaching
            {...props}
            listColumn
            title="Recommended Coaching"
            refreshCoachingData={refreshCoachingData}
          />
        )}
      </div>
    </div>
  );
};

export default CoachingAgenda;
